import React, { useState, useEffect } from 'react'
import { Box, Paper, Text, Divider, Table, Anchor, ActionIcon, Group, Button, Flex } from '@mantine/core'
import { useParams, useNavigate } from 'react-router-dom'
import { IconTrash, IconExternalLink } from '@tabler/icons-react'
import { getAbandonedCartItemsByUser, deleteAbandonedCartItem, clearAbandonedCartItemsByUser } from '../../../../../services/dashboard'
import LoadingData from '../../../../ui/LoadingData'
import NoData from '../../../../ui/NoData'
import { modalDeleteData } from '../../../../ui/Prompt/modalDeleteData'

const labelTable = [
  { label: 'No.', width: 50 },
  { label: 'Product ID', width: 140 },
  { label: 'Product Name', width: 'auto' },
  { label: 'Action', width: 80 }
]

const UserAbandonedCart = () => {
  const params = useParams()
  const userId = params.id
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [cartItems, setCartItems] = useState([])

  const fetchAbandonedCartItems = async () => {
    setLoading(true)
    try {
      const response = await getAbandonedCartItemsByUser(userId)
      if (response?.data) {
        setCartItems(response.data.cartItemsV2ByUser?.data || [])
      } else {
        setCartItems([])
      }
    } catch (error) {
      console.error('Error fetching user abandoned cart items:', error)
      setCartItems([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (userId) {
      fetchAbandonedCartItems()
    }
  }, [userId])

  const handleDelete = (item) => {
    const itemName = item.productName || item.product?.title || `Product ID: ${item.productId}`
    modalDeleteData(
      'Abandoned Cart Item',
      item.id,
      itemName,
      deleteAbandonedCartItem,
      () => fetchAbandonedCartItems()
    )
  }

  const handleClearAll = () => {
    modalDeleteData(
      'All Abandoned Cart Items',
      userId,
      "All items in this user's cart",
      clearAbandonedCartItemsByUser,
      () => fetchAbandonedCartItems()
    )
  }

  const renderTableHead = () => {
    return labelTable.map((val, index) => (
      <Table.Th key={index} width={val.width !== 'auto' ? val.width : ''}>
        {val.label}
      </Table.Th>
    ))
  }

  const renderTableBody = () => {
    if (cartItems.length === 0) {
      return (
        <Table.Tr>
          <Table.Td colSpan={labelTable.length}>
            <NoData />
          </Table.Td>
        </Table.Tr>
      )
    }

    return cartItems.map((item, index) => {
      const productName = item.productName || item.product?.title || '-'

      return (
        <Table.Tr key={item.id || index}>
          <Table.Td>{index + 1}</Table.Td>
          <Table.Td>
            {item.productId ? (
              <Anchor
                onClick={() => navigate(`/product/${item.productId}`)}
                style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                fw={600}
                fz={13}
                c="blue"
              >
                {item.productId}
                <IconExternalLink size={14} stroke={1.5} />
              </Anchor>
            ) : (
              <Text fz={13} c="gray.5">-</Text>
            )}
          </Table.Td>
          <Table.Td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            <Text fz={13} fw={500}>{productName}</Text>
          </Table.Td>
          <Table.Td>
            <ActionIcon
              color="red"
              variant="subtle"
              onClick={() => handleDelete(item)}
              title="Delete item"
            >
              <IconTrash size={16} stroke={1.5} />
            </ActionIcon>
          </Table.Td>
        </Table.Tr>
      )
    })
  }

  return (
    <Box>
      <Paper my="xl" p="md" withBorder shadow="xs" radius="md">
        <Flex justify="space-between" align="center" mb="xs">
          <Text fw={500} fz={16}>
            Latest Abandoned Cart Items
          </Text>
          <Button
            color="red"
            variant="outline"
            size="xs"
            onClick={handleClearAll}
            disabled={cartItems.length === 0}
          >
            Clear All Items
          </Button>
        </Flex>
        <Divider my="xs" />
        <Box my={20}>
          {loading ? (
            <LoadingData />
          ) : (
            <Table.ScrollContainer minWidth={600} type="native">
              <Table highlightOnHover withTableBorder style={{ fontSize: '13px' }}>
                <Table.Thead style={{ backgroundColor: '#f8f9fa' }}>
                  <Table.Tr>{renderTableHead()}</Table.Tr>
                </Table.Thead>
                <Table.Tbody style={{ verticalAlign: 'middle' }}>
                  {renderTableBody()}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Box>
      </Paper>
    </Box>
  )
}

export default UserAbandonedCart
